import React from 'react';
import styled, { keyframes } from 'styled-components';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { PageContext } from '../..';
import { PopItem, VendexPopItMeta } from '../../../models/VendexElement';

const fadeAnimation = keyframes`
 0% { opacity: 0 }
 100% { opacity: 1; }
`;

const Pop = styled.div`
  position: absolute;
  animation-name: ${fadeAnimation};
  animation-duration: 2s;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
`;

type Props = {
  id: string;
  data: VendexPopItMeta;
};

// eslint-disable-next-line react/display-name
const VendexPopIt: React.FC<Props> = ({ id, data }) => {
  const [popItems, setPopItems] = React.useState<string[]>([]);
  const { store } = React.useContext(PageContext);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (data.popItems.length > 0) {
      generateuniqIdList();
      store.SET_DATA(`${data.name}, ${0}`);
    }
  }, [data.popItems]);

  React.useEffect(() => {
    if (popItems.length > 0) {
      startGame();
    }
  }, [popItems]);

  const startGame = () => {
    popItems.forEach((id) => {
      setStyles(id);
    });
  };

  const generateuniqIdList = () => {
    const temp: string[] = [];
    for (let i = 0; i < data.itemsInScreen; i++) {
      temp.push(uuidv4());
    }
    setPopItems(temp);
  };

  const selectRandomItem = (): PopItem => {
    const index = Math.floor(Math.random() * data.popItems.length);
    return data.popItems[index];
  };

  const generateRandomPos = (width: number, height: number) => {
    const { width: parentWidth, height: parentHeight } = (ref.current as HTMLDivElement).getBoundingClientRect();
    const maxX = parentWidth - width - 5;
    const maxY = parentHeight - height - 5;
    const left = Math.floor(Math.random() * maxX);
    const top = Math.floor(Math.random() * maxY);

    return { x: left, y: top };
  };

  const setStyles = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const item = selectRandomItem();
      const { x: left, y: top } = generateRandomPos(item.width, item.height);
      element.style.background = `url(${item.source})`;
      element.style.backgroundSize = 'cover';
      element.style.backgroundRepeat = 'no-repeat';
      element.style.backgroundPosition = 'center';
      element.style.width = `${item.width}px`;
      element.style.height = `${item.height}px`;
      element.style.left = `${left}px`;
      element.style.top = `${top}px`;
      element.style.opacity = '1';
      element.style.visibility = 'visible';
      element.style.pointerEvents = 'none';
      element.style.pointerEvents = 'all';
      element.setAttribute('data-point', `${item.point}`);
      if (item.popSource) {
        element.setAttribute('data-popsource', item.popSource);
      }
    }
  };

  const handleClick = (e: React.MouseEvent | React.TouchEvent, id: string) => {
    e.stopPropagation();
    const element = document.getElementById(id);
    if (element) {
      const point = element.getAttribute('data-point');
      const newScore = point ? parseInt(point) + parseInt(store.data[data.name]) : parseInt(store.data[data.name]);
      store.SET_DATA(`${data.name}, ${newScore}`);
      const popSource = element.getAttribute('data-popsource');
      if (popSource) {
        element.style.background = `url(${popSource})`;
        element.style.backgroundSize = 'cover';
        element.style.backgroundRepeat = 'no-repeat';
        element.style.backgroundPosition = 'center';
        element.style.pointerEvents = 'none';
        setTimeout(() => {
          element.style.visibility = 'hidden';
          element.style.opacity = '0';
          setStyles(id);
        }, 500);
      } else {
        element.style.visibility = 'hidden';
        element.style.opacity = '0';
        setStyles(id);
      }
    }
  };

  return (
    <div ref={ref} id={id}>
      {popItems.map((el) =>
        ReactDOM.createPortal(
          <Pop
            key={el}
            id={el}
            onClick={(e) => handleClick(e, el)}
            onTouchStart={(e) => {
              handleClick(e, el);
            }}
            onTouchMove={(e) => e.preventDefault()}
            onTouchEnd={(e) => e.preventDefault()}
          />,
          document.getElementById(id) as HTMLElement,
          el
        )
      )}
    </div>
  );
};

export default VendexPopIt;
